import * as React from "react";
import { observer } from "mobx-react";
import { CSSTransition } from "react-transition-group";
import {
  Grid,
  Typography,
  Input,
  IconButton,
  Button,
  withStyles,
  CircularProgress,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import PlaceWithMapField, { initGoogleMap } from "./PlaceWithMapField";
import { DevelopmentStore } from "modules/development/DevelopmentStore";
import { DevelopmentService } from "modules/development/DevelopmentService";
import i18n from "i18n";
import { Redirect } from "react-router";
import { LoginStore } from "modules/login/LoginStore";
const styles = (theme) => ({
  container: {
    position: "absolute" as any,
    left: 0,
    top: 0,
    zIndex: 3 as any,
    height: "100%",
    display: "grid",
    gridTemplateRows: "auto 1fr",
    "& > :last-child": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  formContainer: {
    position: "relative" as any,
    textAlign: "center" as any,
    height: "calc(100vh - 128px)",
    overflowY: "auto" as any,
    paddingBottom: "1rem",
  },
  header: {
    backgroundColor: theme.palette.secondary.light,
    borderBottom: "1px solid #f2f2f2",
    height: "64px",
  },
  closeIcon: {
    right: "20px",
    top: "14px",
  },
  title: {
    lineHeight: "64px",
    fontSize: "16px",
    fontFamily: "Raleway",
    display: "inline-block",
    cursor: "pointer",
  },
  newProject: {
    left: "100%",
    top: 0,
    cursor: "auto",
  },
  closeLayerIcon: {
    right: 20,
    top: 20,
  },
  backLayerIcon: {
    left: 20,
    top: 20,
    float: "left" as any,
  },
  layerSecondTitleContainer: {
    marginTop: 36,
  },
  layerSecondTitleMessage: {
    fontSize: "26px",
    fontFamily: "Raleway",
    fontWeight: 400,
    marginTop: 25,
  },
  layerTitle: {
    fontSize: "12px",
    fontFamily: "Roboto",
    fontWeight: 400,
    letterSpacing: "1.4px",
  },
  input: {
    fontFamily: "Roboto",
    fontSize: "23px",
    fontWeight: 400,
    width: "612px",
    margin: "0 auto",
    marginTop: "25px",
    marginBottom: "25px",
    display: "block",
  },
  transitionEnter: {
    opacity: 0.01,
  },
  transitionEnterActive: {
    opacity: 1,
    transition: "opacity 300ms ease-in",
  },
  transitionLeave: {
    opacity: 1,
  },
  transitionLeaveActive: {
    opacity: 0.01,
    transition: "opacity 300ms ease-in",
  },
  cardDevelopment: {
    backgroundColor: "white",
    transition: "box-shadow 300ms ease-in",
    width: 230,
    height: 230,
    margin: "25px 15px",
    float: "left" as any,
    "&:hover": {
      boxShadow:
        "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
    },
  },
  textCard: {
    width: "100%",
    paddingTop: 25,
    height: 70,
    textAlign: "center" as any,
    fontSize: "16px",
    fontFamily: "Roboto",
    fontWeight: 500,
  },
  useProjectDisabled: {
    position: "relative" as any,
    top: 70.5,
    color: "#000",
    margin: "0px auto",
    width: 210,
    fontSize: "16px",
    fontFamily: "Raleway",
    fontWeight: 500,
  },
});

type IProps = {
  classes: any;
};

@observer
class LayerAddDevelopment extends React.Component<IProps> {
  state = {
    name: "",
    disabled: false,
    index: 0,
    address: "",
  };

  componentDidMount() {
    initGoogleMap();
  }

  handleInputChangeEvent = (e) => this.setState({ name: e.target.value });

  handleAddDevelopment = () => {
    this.setState({ disabled: true }, async () => {
      DevelopmentStore.loading = true;
      let id = await DevelopmentService.AddDevelopment(
        this.state.name,
        this.state.address
      );
      this.state.disabled = false;
      DevelopmentStore.layerAdd = false;
      DevelopmentStore.redirectId = id;
      DevelopmentStore.loading = false;
    });
  };

  /**
   * Esta função é responsável por enviar os dados do projeto a ser copiado para cloud function
   * na qual será feito a duplicação do projeto.
   * Caso receba um retorno com sucesso, ela fará a verificação se precisa atualizar a EAP e
   * depois irá definir o ID para abrir o projeto cópia.
   * @async
   * @method handleAddDevelopmentFromCopy
   * @category Developments
   * @param {any} dev - Projeto a ser copiado
   * @returns {Promise<void>}
   */
  async handleAddDevelopmentFromCopy(dev): Promise<void> {
    DevelopmentStore.loadingCopy = true;
    DevelopmentStore.openCreateDevelopmentDialog = false;
    dev.projectName = DevelopmentStore.projectName;

    let user = LoginStore.userAuth;
    if (user) {
      dev.data.users = { [user.uid]: 0 };
      dev.data.ownerName = user.displayName;
      dev.data.ownerPhoto = user.photoURL;
    }
    dev.data.address = this.state.address;

    let result: any = await DevelopmentService.CopyDevelopment(dev);

    DevelopmentStore.layerAdd = false;
    DevelopmentStore.loading = false;
    DevelopmentStore.clearDevelopmentCopied(); // PRUBK-805
    if (
      result.wbsRowIdsFromModelMap &&
      result.wbsRowIdsFromModelMap.length > 0
    ) {
      DevelopmentStore.needUpdateEapValues = true;
    }
    DevelopmentStore.redirectId = result.newDevelopmentId;
  }

  handleCloseLayerAdd = () => {
    this.setState({ index: 0, address: "" });
    DevelopmentStore.isEditAddress = false;
    DevelopmentStore.layerAdd = false;
  };

  handleNext = () => {
    this.setState({ index: 1 });
  };

  handleBack = () => {
    this.setState({ index: 0, address: "" });
    DevelopmentStore.isEditAddress = false;
  };

  handleChangeIndex = (index) => {
    this.setState({ value: index });
  };

  handleChangeAddress = (address) => {
    this.setState({ address });
  };

  handleOpenCreateDevelopment = () => {
    DevelopmentStore.openCreateDevelopmentDialog = true;
    DevelopmentStore.projectName = this.state.name;
  };

  handlecreateDevelopment = async () => {
    if (DevelopmentStore.isEditAddress == false) {
      if (DevelopmentStore.isCopy == true) {
        // Cópia
        let dev = DevelopmentStore.fromCopy;
        DevelopmentStore.projectName = this.state.name;
        this.handleAddDevelopmentFromCopy(dev);
      } else {
        // Novo
        this.handleAddDevelopment();
      }
    } else {
      // Edição
      await DevelopmentService.EditAddress(
        DevelopmentStore.isEditAddress.id,
        this.state.address
      );
      DevelopmentStore.layerAdd = false;
      DevelopmentStore.loading = false;
    }
  };

  componentDidUpdate() {
    if (
      DevelopmentStore.isEditAddress &&
      DevelopmentStore.isEditAddress.data.address != "" &&
      DevelopmentStore.firstLoad
    ) {
      this.setState({ address: DevelopmentStore.isEditAddress.data.address });
      DevelopmentStore.firstLoad = false;
    } else {
      if (DevelopmentStore.firstLoad) {
        this.setState({ address: "" });
        DevelopmentStore.firstLoad = false;
      }
    }
  }

  render() {
    const { classes } = this.props,
      { disabled } = this.state;
    if (DevelopmentStore.redirectId) {
      return <Redirect to={`/${DevelopmentStore.redirectId}/WBS`} />;
    }
    let editAdrress = DevelopmentStore.isEditAddress == false ? false : true;

    return (
      <CSSTransition
        in={DevelopmentStore.layerAdd}
        enter={true}
        exit={true}
        timeout={300}
        classNames={{
          enter: classes.transitionEnter,
          enterActive: classes.transitionEnterActive,
          exit: classes.transitionLeave,
          exitActive: classes.transitionLeaveActive,
        }}
        unmountOnExit
      >
        <Grid container className={classes.container}>
          <Grid container item xs={12} className={classes.header}>
            <Grid item xs={2} style={{ textAlign: "center" }}>
              <Typography
                className={classes.title}
                style={{ textAlign: "center", position: "relative" }}
                onClick={this.handleCloseLayerAdd}
              >
                {i18n.menu.development}
                <span
                  className={classes.newProject}
                  style={{
                    position: "absolute",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  &ensp;{">"}&ensp;
                  {DevelopmentStore.loadingWbsUpdate &&
                  DevelopmentStore.refactorDevelopName ? (
                    <span style={{ fontWeight: 700, overflow: "hidden" }}>
                      {DevelopmentStore.refactorDevelopName}
                    </span>
                  ) : editAdrress == false ? (
                    <span style={{ fontWeight: 700, overflow: "hidden" }}>
                      {i18n.developments.layerAdd.new}
                    </span>
                  ) : (
                    <span style={{ fontWeight: 700, overflow: "hidden" }}>
                      {i18n.developments.layerAdd.edit}
                    </span>
                  )}
                </span>
              </Typography>
            </Grid>
          </Grid>
          <div className={classes.formContainer}>
            <IconButton
              onClick={this.handleCloseLayerAdd}
              className={classes.closeLayerIcon}
              style={{ position: "absolute" }}
              disabled={disabled}
            >
              <Close fontSize="small" />
            </IconButton>

            <div style={{ marginTop: "40px" }}>
              <Typography className={classes.layerTitle} component="h2">
                {editAdrress == false
                  ? i18n.developments.layerAdd.title
                  : i18n.developments.layerAdd.editAdrress}
              </Typography>
            </div>

            <div style={{ position: "relative", textAlign: "center" }}>
              {editAdrress ? (
                <div
                  style={{
                    marginTop: "38px",
                    marginBottom: "30px",
                    textAlign: "center",
                    fontSize: "18px",
                    fontWeight: 500,
                    fontFamily: "Roboto",
                    letterSpacing: "0px",
                    color: "#000000",
                    opacity: 1,
                  }}
                >
                  {DevelopmentStore.isEditAddress &&
                    DevelopmentStore.isEditAddress.data.name}
                </div>
              ) : (
                <Input
                  autoFocus
                  inputProps={{
                    style: { textAlign: "center" },
                    maxLength: "30",
                  }}
                  className={classes.input}
                  placeholder={i18n.developments.layerAdd.placeholder}
                  onChange={this.handleInputChangeEvent}
                  disabled={disabled}
                />
              )}
              <PlaceWithMapField
                label={i18n.developments.layerAdd.placeholderAddress}
                address={this.state.address}
                onChange={this.handleChangeAddress}
                disabled={disabled}
              />
              <Button
                variant="contained"
                color="primary"
                disabled={
                  editAdrress == false ? this.state.name.length == 0 : false
                }
                onClick={this.handlecreateDevelopment}
              >
                {editAdrress == false
                  ? i18n.developments.layerAdd.start
                  : i18n.developments.layerAdd.save}
              </Button>
            </div>

            {(DevelopmentStore.loading ||
              DevelopmentStore.loadingCopy ||
              DevelopmentStore.loadingWbsUpdate) && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  backgroundColor: "rgba(250, 250, 250, 1)",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CircularProgress
                  size={100}
                  style={{ margin: "0 auto 10px", color: "#9FB408" }}
                />
                <Typography
                  style={{
                    position: "absolute",
                    marginLeft: DevelopmentStore.loadingWbsUpdate
                      ? "calc(50% - 220px)"
                      : "calc(50% - 64px)",
                    marginTop: "140px",
                  }}
                >
                  {DevelopmentStore.loadingWbsUpdate
                    ? i18n.developments.loadingUpdateWbs
                    : i18n.developments.loading}
                </Typography>
              </div>
            )}
          </div>
        </Grid>
      </CSSTransition>
    );
  }
}

export default withStyles(styles)(LayerAddDevelopment);



// WEBPACK FOOTER //
// ./src/modules/development/components/layouts/LayerAddDevelopment.tsx